import { useMutation } from '@tanstack/react-query';
import useNotificationSettings, { notificationSettingsQueryOptions } from '@/hooks/settings/use-notification-settings';
import type { NotificationSettings } from '@/models/mine/notification-setting';
import type { RespondedType, ResponseError } from '@/models/response';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { queryClient } from '@/utils/query-client';

type MutateContext =
  | {
      previousSettings?: NotificationSettings;
    }
  | undefined;

export default function useUpdateNotificationSettings() {
  const { data: notificationSettings } = useNotificationSettings();
  const mutation = useMutation<RespondedType, ResponseError, NotificationSettings>({
    mutationKey: ['/UpdateNotificationSettings'],
    onMutate: async (newSettings) => {
      await queryClient.cancelQueries({ queryKey: notificationSettingsQueryOptions.queryKey });

      const previousSettings = queryClient.getQueryData<NotificationSettings>(
        notificationSettingsQueryOptions.queryKey,
      );

      queryClient.setQueryData(notificationSettingsQueryOptions.queryKey, newSettings);

      return { previousSettings };
    },
    onError: (err, _, context) => {
      const typedContext = context as MutateContext;
      if (typedContext?.previousSettings) {
        queryClient.setQueryData(notificationSettingsQueryOptions.queryKey, typedContext.previousSettings);
      }

      addToast({
        type: 'error',
        message: err.message || 'errors.error',
        translateMessage: !err.message,
      });
    },
    mutationFn: (params) => fetcher('/UpdateNotificationSettings', params, { validResponseCodes: [0] }),
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: data?.Msg ?? error?.message,
      }),
  });

  const updateNotificationSettings = <K extends keyof NotificationSettings>(
    key: K,
    value: NotificationSettings[K],
  ): void => {
    if (notificationSettings) {
      mutation.mutate({
        ...notificationSettings,
        [key]: value,
      });
    }
  };

  return {
    updateNotificationSettings,
    ...mutation,
  };
}
